import { useCallback, useState } from 'react'
import { ThemeProvider } from 'styled-components'
import { ConfirmOtherVenueNavigationModal } from '@/Reservations/Search/ConfirmOtherVenueNavigationModal'
import type { ReservationHoldErrorResponse } from '@sevenrooms/domain'
import { ReservationWidget } from '@sevenrooms/domain/constants'
import { useForm, useWatchMany } from '@sevenrooms/form'
import { useLocales } from '@sevenrooms/locales'
import { DateOnly, TimeOnly } from '@sevenrooms/timepiece'
import { Form } from '@sevenrooms/ui-kit/form'
import { Box, VStack, Window } from '@sevenrooms/ui-kit/layout'
import { Text } from '@sevenrooms/ui-kit/typography'
import { ReservationHoldModal } from '../../../components/Modals/ResrvationHoldModal'
import {
  type AvailabilityTimeWithUpSellCost,
  useAvailabilityDates,
  useCachedCreateReservationHold,
  useExperiencesWithAvailability,
  useGroupOrCrossSellWidget,
  useOtherVenueTheme,
  useReservationNavigation,
  useReservationsRoute,
  useVenue,
  useWidgetLanguage,
  useWidgetSettings,
} from '../../../hooks'
import { reservationWidgetMessages } from '../../../reservationWidgetMessages'
import { useModals, useReservationFormState } from '../../../store'
import {
  confirmTimeslotGroupBooking,
  finishSearchPage,
  selectTimeslot,
  trackTealiumChangeVenue,
  updateDateSearchParams,
  updatePartySizeSearchParams,
  updateTimeSearchParams,
} from '../../../utils'
import { ExperienceDetailsModal } from '../ExperienceDetailsModal'
import { Experiences } from '../Experiences'
import { ReservationSearchComponent } from '../ReservationSearchComponent'
import { StyledSearchInputGridContainer } from '../ReservationSearchInputGroup'
import { getReservationHoldErrorMessage } from '../utils'
import { PrivateEventsCrossSell } from './PrivateEventsCrossSell'
import { PrivateEventsGroupWidget } from './PrivateEventsGroupWidget'
import { usePrivateEventsSearchFormSchema } from './PrivateEventsTab.zod'
import type { ExperienceWithAvailability } from '../../../hooks/useAvailability'
import type { OtherVenueInfo } from '@/domain'

interface PrivateEventsTabProps {
  showTabs?: boolean
}

export function PrivateEventsTab({ showTabs }: PrivateEventsTabProps) {
  const { formatMessage } = useLocales()
  const { urlKey, currencyCode, id: venueId, startOfDayTime, name: venueName } = useVenue()
  const { activeModal, previousActiveModal, showModal, hideModal, showErrorModal } = useModals()
  const { navigateNext: navigateNextStep, disableStep, enableStep } = useReservationNavigation()
  const { reservationHoldEnabled } = useWidgetSettings()
  const { selectedLanguage } = useWidgetLanguage()
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<AvailabilityTimeWithUpSellCost>()
  const [selectedExperience, setSelectedExperience] = useState<ExperienceWithAvailability>()
  const [selectedOtherVenue, setSelectedOtherVenue] = useState<OtherVenueInfo | undefined>()
  const { channel } = useReservationsRoute()
  const { isGroupWidget, isCrossSellWidget, venuesInfo, isFetchingVenuesInfo } = useGroupOrCrossSellWidget()

  const theme = useOtherVenueTheme(selectedOtherVenue)

  const { isFetching: isDatesFetching, isDayBlocked } = useAvailabilityDates()

  const [createReservationHold] = useCachedCreateReservationHold()

  const {
    formState: { ...searchFormData },
    updateFormState,
  } = useReservationFormState()

  const privateEventsSearchFormSchema = usePrivateEventsSearchFormSchema()
  const form = useForm(privateEventsSearchFormSchema, {
    defaultValues: {
      privateEventsPartySize: searchFormData.privateEventsPartySize,
      privateEventsStartDate: searchFormData.privateEventsStartDate
        ? DateOnly.from(searchFormData.privateEventsStartDate).toJsDate()
        : undefined,
      privateEventsStartTime: searchFormData.privateEventsStartTime,
      privateEventsHaloTimeIntervalMinutes: searchFormData.privateEventsHaloTimeIntervalMinutes,
    },
  })
  const { field, handleSubmit, setValue } = form
  const [privateEventsPartySize, privateEventsStartDate, privateEventsStartTime, privateEventsHaloTimeIntervalMinutes] = useWatchMany(
    field,
    ['privateEventsPartySize', 'privateEventsStartDate', 'privateEventsStartTime', 'privateEventsHaloTimeIntervalMinutes']
  )

  const {
    data: experiences,
    isFetching: isExperiencesFetching,
    isError: isExperiencesFetchingError,
  } = useExperiencesWithAvailability({
    venue: venueId,
    partySize: privateEventsPartySize || undefined,
    startDate: privateEventsStartDate ? DateOnly.fromDate(privateEventsStartDate).toIso() : undefined,
    startTime: privateEventsStartTime,
    haloTimeIntervalMinutes: privateEventsHaloTimeIntervalMinutes,
    startOfDayTime,
    clientId: searchFormData.clientId,
    selectedLangCode: selectedLanguage,
    experienceType: 'PDR',
    skipRequests: isGroupWidget,
  })

  const navigateNext = useCallback(
    async (timeslot: AvailabilityTimeWithUpSellCost, venueUrlKey?: string, query?: { [key: string]: string }) => {
      const { accessPersistentId, shiftPersistentId, time, timeIso } = timeslot
      const date = DateOnly.from(timeIso).toIso()
      finishSearchPage(urlKey)
      if (!venueUrlKey && reservationHoldEnabled) {
        showModal('reservationHold')
        try {
          await createReservationHold({
            accessPersistentId,
            channel: channel || ReservationWidget.SevenRoomsWidgetChannel,
            date,
            partySize: privateEventsPartySize as number,
            shiftPersistentId,
            time,
            venueId,
            trackingSlug: searchFormData.trackingSlug,
            clientId: searchFormData.clientId,
          }).unwrap()
        } catch (err) {
          const errorMessage = getReservationHoldErrorMessage(err as ReservationHoldErrorResponse, formatMessage)
          showErrorModal(errorMessage, undefined, true)
        }
        hideModal()
      }
      navigateNextStep(venueUrlKey, query)
    },
    [
      urlKey,
      reservationHoldEnabled,
      navigateNextStep,
      showModal,
      hideModal,
      createReservationHold,
      channel,
      privateEventsPartySize,
      venueId,
      searchFormData.trackingSlug,
      searchFormData.clientId,
      formatMessage,
      showErrorModal,
    ]
  )

  const onTimeSlotClick = useCallback(
    async (timeSlot: AvailabilityTimeWithUpSellCost, experience?: ExperienceWithAvailability, selectedVenue?: OtherVenueInfo) => {
      await handleSubmit(data => {
        const { privateEventsPartySize, privateEventsStartDate } = data
        if (!privateEventsPartySize || !privateEventsStartDate) {
          return
        }
        const date = DateOnly.fromDate(privateEventsStartDate).toIso()

        // update state when other availability is selected.
        updateFormState({
          privateEventsPartySize,
          privateEventsStartDate: date,
          selectedTimeSlot: timeSlot,
          selectedExperience: experience,
          selectedOtherVenue: selectedVenue,
        })
        selectTimeslot(urlKey, date, timeSlot.time, privateEventsPartySize)
        if (timeSlot.upsellCategories && timeSlot.upsellCategories.length > 0) {
          enableStep('upgrades')
        } else {
          disableStep('upgrades')
        }
      })()

      setSelectedTimeSlot(timeSlot)
      setSelectedExperience(experience)
      setSelectedOtherVenue(selectedVenue)
      showModal('moreExperienceDetails')
    },
    [disableStep, enableStep, handleSubmit, showModal, updateFormState, urlKey]
  )

  const onMoreDetailsClick = useCallback(
    (experience: ExperienceWithAvailability) => {
      setSelectedExperience(experience)
      showModal('moreExperienceDetails')
    },
    [showModal]
  )

  const onConfirmAvailabilitySelect = useCallback(
    (timeSlot: AvailabilityTimeWithUpSellCost) => {
      const startDate = DateOnly.from(timeSlot.timeIso).toIso()
      const partySize = privateEventsPartySize as number
      updateFormState({
        startDate,
      })
      confirmTimeslotGroupBooking(selectedOtherVenue?.urlKey ?? urlKey, startDate, timeSlot.time, partySize)

      if (timeSlot.accessPersistentId && timeSlot?.timeIso) {
        const timeIso = TimeOnly.from(timeSlot.timeIso).toHoursMinutesIso()
        const otherVenueURLKey = selectedOtherVenue?.urlKey
        if (otherVenueURLKey) {
          navigateNext(timeSlot, selectedOtherVenue?.urlKey, {
            date: timeSlot.timeIso,
            party_size: partySize.toString(),
            timeslot_id: timeSlot.accessPersistentId,
            timeslot_time: timeIso,
            primary_venue_id: venueId,
            ...(selectedExperience?.id ? { timeslot_experience_id: selectedExperience.id } : {}),
          })
        } else {
          navigateNext(timeSlot)
        }
      } else {
        // This should not happen. If so, there's a bug somewhere.
        // eslint-disable-next-line no-console
        console.error('A timeslot must be selected.')
      }
    },
    [navigateNext, privateEventsPartySize, selectedExperience?.id, selectedOtherVenue?.urlKey, updateFormState, urlKey, venueId]
  )

  const onMoreDetailsModalClose = useCallback(() => {
    hideModal()
    setSelectedExperience(undefined)
    setSelectedTimeSlot(undefined)
  }, [hideModal])

  const onPartySizeChange = useCallback(
    (value: number | null) => {
      updateFormState({ privateEventsPartySize: value })
      if (value) {
        updatePartySizeSearchParams(urlKey, value)
      }
    },
    [updateFormState, urlKey]
  )

  const onPrivateEventsPartySizeChange = useCallback(
    (value: number) => {
      setValue('privateEventsPartySize', value)
      onPartySizeChange(value)
    },
    [onPartySizeChange, setValue]
  )

  const onStartDateChange = useCallback(
    (value: Date | undefined) => {
      updateFormState({ privateEventsStartDate: value ? DateOnly.fromDate(value).toIso() : undefined })
      if (value) {
        updateDateSearchParams(urlKey, DateOnly.fromDate(value).toIso())
      }
    },
    [updateFormState, urlKey]
  )

  const onPrivateEventsStartDateChange = useCallback(
    (value: Date) => {
      setValue('privateEventsStartDate', value)
      onStartDateChange(value)
    },
    [onStartDateChange, setValue]
  )

  const handleFilterTimeChange = ({ startTime, haloTimeIntervalMinutes }: { startTime: string; haloTimeIntervalMinutes: number }) => {
    setValue('privateEventsStartTime', startTime)
    setValue('privateEventsHaloTimeIntervalMinutes', haloTimeIntervalMinutes)
    updateFormState({
      privateEventsStartTime: startTime,
      privateEventsHaloTimeIntervalMinutes: haloTimeIntervalMinutes,
    })
    updateTimeSearchParams(urlKey, startTime, haloTimeIntervalMinutes)
    hideModal()
  }

  const updateSelectedTimeslotInfo = useCallback(
    (timeSlot: AvailabilityTimeWithUpSellCost | undefined, venue: OtherVenueInfo | undefined) => {
      setSelectedTimeSlot(timeSlot)
      setSelectedOtherVenue(venue)
    },
    []
  )

  const onClearTimeFilter = useCallback(() => {
    setValue('privateEventsStartTime', ReservationWidget.AllTimesOption)
  }, [setValue])

  const onModalBackClick = useCallback(() => {
    showModal('viewAllExperienceDates')
  }, [showModal])

  const onSelectedTimeslotModalClose = useCallback(() => {
    hideModal()
    updateSelectedTimeslotInfo(undefined, undefined)
  }, [hideModal, updateSelectedTimeslotInfo])

  return (
    <>
      {showTabs && (
        <Box mt="sm" mb="m">
          <Text textStyle="h1">{formatMessage(reservationWidgetMessages.resWidgetPrivateEventsGroupBookingsTabHeader)}</Text>
        </Box>
      )}
      <VStack spacing="sm" mt={showTabs ? 'none' : 'm'}>
        <StyledSearchInputGridContainer>
          <Form {...form} onSubmit={() => {}} onInvalid={() => {}}>
            <ReservationSearchComponent
              isDatesFetching={isDatesFetching}
              isDayBlocked={isDayBlocked}
              partySize={field.prop('privateEventsPartySize')}
              onPartySizeChange={onPartySizeChange}
              startDate={field.prop('privateEventsStartDate')}
              onStartDateChange={onStartDateChange}
              startTime={privateEventsStartTime}
              haloTimeIntervalMinutes={privateEventsHaloTimeIntervalMinutes}
              onTimeChange={handleFilterTimeChange}
              withEmptySelectOption
            />
          </Form>
        </StyledSearchInputGridContainer>
        {isGroupWidget ? (
          <PrivateEventsGroupWidget
            isLoading={isFetchingVenuesInfo}
            venuesInfo={venuesInfo}
            partySize={privateEventsPartySize ?? undefined}
            startDate={privateEventsStartDate}
            startTime={privateEventsStartTime}
            haloTimeIntervalMinutes={privateEventsHaloTimeIntervalMinutes}
            onTimeSlotClick={(slot: AvailabilityTimeWithUpSellCost, experience: ExperienceWithAvailability, venue: OtherVenueInfo) =>
              onTimeSlotClick(slot, experience, venue)
            }
            onMoreDetailsClick={onMoreDetailsClick}
            onClearTimeFilter={onClearTimeFilter}
          />
        ) : (
          <VStack spacing="lm">
            <Experiences
              descriptionType="short"
              partySize={privateEventsPartySize ?? undefined}
              startDate={privateEventsStartDate ? DateOnly.fromDate(privateEventsStartDate).toIso() : undefined}
              startTime={privateEventsStartTime}
              haloTimeIntervalMinutes={privateEventsHaloTimeIntervalMinutes}
              experiences={experiences}
              onStartDateChange={onPrivateEventsStartDateChange}
              onPartySizeChange={onPrivateEventsPartySizeChange}
              isLoading={isExperiencesFetching}
              isError={isExperiencesFetchingError}
              onTimeSlotClick={onTimeSlotClick}
              onMoreDetailsClick={onMoreDetailsClick}
              onClearTimeFilter={onClearTimeFilter}
              onRequestModalBackClick={onModalBackClick}
              showNextAvailableDay
              isPrivateEvents
            />
            {isCrossSellWidget && (
              <PrivateEventsCrossSell
                isLoading={isFetchingVenuesInfo}
                venuesInfo={venuesInfo}
                partySize={privateEventsPartySize ?? undefined}
                startDate={privateEventsStartDate}
                startTime={privateEventsStartTime}
                haloTimeIntervalMinutes={privateEventsHaloTimeIntervalMinutes}
                onTimeSlotClick={(slot: AvailabilityTimeWithUpSellCost, experience: ExperienceWithAvailability, venue: OtherVenueInfo) =>
                  onTimeSlotClick(slot, experience, venue)
                }
                onMoreDetailsClick={onMoreDetailsClick}
                onClearTimeFilter={onClearTimeFilter}
              />
            )}
          </VStack>
        )}
      </VStack>
      {activeModal === 'moreExperienceDetails' && selectedExperience && (
        <Window active>
          <ThemeProvider theme={theme}>
            <ExperienceDetailsModal
              experience={selectedExperience}
              currencyCode={currencyCode}
              timeSlot={selectedTimeSlot}
              onBack={previousActiveModal === 'viewAllExperienceDates' ? onModalBackClick : undefined}
              onClose={onMoreDetailsModalClose}
              onConfirmSelect={timeslot => {
                if (selectedOtherVenue && selectedOtherVenue?.urlKey !== urlKey && !isGroupWidget) {
                  showModal('confirmOtherVenueNavigation')
                } else {
                  onConfirmAvailabilitySelect(timeslot)
                }
              }}
            />
          </ThemeProvider>
        </Window>
      )}
      {activeModal === 'reservationHold' && (
        <Window active>
          <ReservationHoldModal />
        </Window>
      )}
      {activeModal === 'confirmOtherVenueNavigation' && !!selectedOtherVenue && (
        <Window active>
          <ThemeProvider theme={theme}>
            <ConfirmOtherVenueNavigationModal
              fromVenue={venueName}
              toVenue={selectedOtherVenue.venueName}
              onConfirm={() => {
                if (selectedTimeSlot) {
                  trackTealiumChangeVenue(selectedOtherVenue.urlKey, selectedOtherVenue.venueName, selectedOtherVenue.countryCode)
                  onConfirmAvailabilitySelect(selectedTimeSlot)
                }
              }}
              onClose={onSelectedTimeslotModalClose}
            />
          </ThemeProvider>
        </Window>
      )}
    </>
  )
}
